import React, { FC, useState } from "react"
import * as Yup from "yup"
import { CreateZammadTicket, emailRegex } from "../../utils/core"
import { TextArea, TextInput } from "../../utils/TextControl";
import { useTranslation } from "../../plugins/i18next/ea-react-i18next";
import Button from "../Start/Button";

// Define Yup validation schema
const validationSchema = Yup.object({
  email: Yup.string().email().matches(emailRegex, "Invalid email address").required("Required field"),
  message: Yup.string().required("Required field"),
})

export const RequestForm: FC = () => {
  const [isLoading, setLoading] = useState(false);
  const [isExecutingRequest, setIsExecutingRequest] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errors, setErrors] = useState({});

  const { t } = useTranslation("components", { keyPrefix: 'zammad.requestForm' });

  const [formValues, setFormValues] = useState<{
    email: string
    message: string
    name: string
  }>({
    email: "",
    message: "",
    name: ""
  })

  const submit = async () => {
    try {
      // Validate the form using the Yup validation schema
      await validationSchema.validate(formValues, { abortEarly: false });
      setIsExecutingRequest(true);
      await CreateZammadTicket(
        formValues.email,
        `Website - Support request by ${formValues.name}`,
        formValues.message
      );
      setIsSuccess(true);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        error.inner.forEach((currentError) => {
          if (currentError.path) {
            newErrors[currentError.path] = currentError.message;
          }
        });
        setErrors(newErrors);
      } else {
        setIsError(true);
      }
    } finally {
      setIsExecutingRequest(false);
    }
  };


  const handleChange = async (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target
    const newValues = { ...formValues, [name]: value };
    setFormValues(newValues)

    try {
      // Validate the form using the Yup validation schema
      await validationSchema.validate(newValues, { abortEarly: false })
      setErrors({});
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        error.inner.forEach((currentError) => {
          if (currentError.path)
            newErrors[currentError.path] = currentError.message;
        });
        setErrors(newErrors);
      }
    }
  }

  if (isLoading) {
    return (
      <div className="spinner-border" role="status">
        <span className="visually-hidden"></span>
      </div>
    )
  }

  if (isSuccess) {
    return (
      <div className="alert alert-success" role="alert">
        {t('isSuccess')}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="alert alert-danger" role="alert">
        {t('isError')}
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="form-group">
        <TextInput
          name="name"
          type="text"
          placeholder={t("name")}
          value={formValues.name}
          onChange={handleChange}
          error={(errors as any).name}
          className="form-control"
        />
        <TextInput
          name="email"
          type="email"
          placeholder="jane@yourmail.com"
          value={formValues.email}
          onChange={handleChange}
          error={(errors as any).email}
          className="form-control"
        />
      </div>
      <div className="form-group">
        <TextArea
          name="message"
          placeholder={t("message")}
          value={formValues.message}
          onChange={handleChange}
          error={(errors as any).message}
          className="form-control"
        />
      </div>
      <Button onClick={submit}
        text={t("getHelp")}
        disabled={isLoading || isSuccess || isError || isExecutingRequest}
        icon="flaticon-tick" />
    </React.Fragment>
  )
}
